<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import CoreIcon from '@core/core-icon.vue';
import { useNumber } from '@composables/number';
import type { PaymentTariff } from '@store/api/interfaces';

/**
 * Карточка тарифа: срок и цена за месяц в первой строке, полная цена (со
 * старой зачёркнутой, если есть скидка) во второй, ниже — что входит:
 * безлимитный основной трафик и резервные серверы с нормой ГБ в месяц.
 */
const { t } = useI18n();
const { withSpaces } = useNumber();

const props = defineProps<{
	tariff: PaymentTariff;
	selected: boolean;
	oneMonthPrice: number;
	isBestValue: boolean;
	anySelected: boolean;
}>();

defineEmits<{
	(e: 'select'): void;
}>();

const currencySymbol = computed((): string => {
	return t(`common.currency.${props.tariff.cost.currency}`);
});

const hasPromo = computed((): boolean => {
	return !!props.tariff.promo_code && !!props.tariff.cost_without_discount;
});

const giftMonths = computed((): number | null => {
	if (props.tariff.promo_code?.type === 'gift_month' && props.tariff.promo_code.gift_months) {
		return props.tariff.promo_code.gift_months;
	}
	return null;
});

// Price per month (cost already includes promo discount from server)
const perMonth = computed((): number => {
	const totalMonths = props.tariff.months + (giftMonths.value ?? 0);
	return Math.ceil(props.tariff.cost.amount / totalMonths);
});

const periodName = computed((): string => {
	const gift = giftMonths.value;
	if (props.tariff.months === 12) {
		if (gift) {
			return t('payment.period_year_gift', { n: 1, gift });
		}
		return t('payment.period_year', 1);
	}
	if (gift) {
		return `${props.tariff.months} + ${gift} ${t('payment.period_gift_word', gift)}`;
	}
	return t('payment.period_month', props.tariff.months);
});

const pricePerMonthFormatted = computed((): string => {
	return t('payment.per_month', { price: withSpaces(perMonth.value) + currencySymbol.value });
});

// Total price (real cost of the tariff)
const totalPriceFormatted = computed((): string => {
	return withSpaces(props.tariff.cost.amount) + ' ' + currencySymbol.value;
});

// Original total price before promo (for strikethrough)
const originalTotalFormatted = computed((): string | null => {
	if (!hasPromo.value || !props.tariff.cost_without_discount) return null;
	return withSpaces(props.tariff.cost_without_discount.amount) + ' ' + currencySymbol.value;
});

// Полная цена нужна, когда она отличается от цены за месяц: срок больше
// месяца или есть скидка (тогда рядом зачёркнутая старая). У месяца без
// скидки она повторила бы первую строку.
const showTotal = computed((): boolean => {
	return props.tariff.months > 1 || !!giftMonths.value || !!originalTotalFormatted.value;
});

// Total discount: always compared to original 1-month price (before any promo)
// For 1 month with 20% promo: 1 - 232/290 = 20%
// For 3 months with 20% promo: 1 - 211/290 = 27% (promo + period savings combined)
// For 3 months without promo: 1 - 263/290 = 9% (period savings only)
// Подарочный месяц снижает цену за месяц, поэтому скидка появляется и у него.
const discountPercent = computed((): number | null => {
	const basePerMonth = props.oneMonthPrice;
	if (!basePerMonth || basePerMonth <= 0) return null;
	const discount = Math.round((1 - perMonth.value / basePerMonth) * 100);
	return discount > 0 ? discount : null;
});

// Best value gradient border: only when no other tariff is selected
const showBestBorder = computed((): boolean => {
	return props.isBestValue && !props.selected && !props.anySelected;
});

// Резервный трафик, который бот начисляет каждый месяц по этой подписке.
// null (или отсутствие поля на старом бэкенде) — резерв в тариф не входит,
// и строки про резервные серверы в карточке нет.
const reserveMonthlyGb = computed((): number | null => {
	return props.tariff.reserve_monthly_gb ?? null;
});
</script>

<template>
	<div
		class="tariff-card"
		:class="{
			'tariff-card_gradient-border': showBestBorder,
			'tariff-card_selected': selected,
		}"
		@click="$emit('select')"
	>
		<!-- Content sits above ::before -->
		<div class="tariff-card__inner">
			<div class="tariff-card__row">
				<div class="tariff-card__period">
					<span class="tariff-card__period-name">{{ periodName }}</span>

					<!-- Small discount badge (not best value) -->
					<span
						v-if="discountPercent && !isBestValue"
						class="tariff-card__badge-small"
					>
						<span class="tariff-card__badge-small-text">
							{{ t('payment.discount', { n: discountPercent }) }}
						</span>
					</span>

					<!-- Large discount badge (best value only) -->
					<span
						v-if="discountPercent && isBestValue"
						class="tariff-card__badge-large"
					>
						<span class="tariff-card__badge-large-bg"></span>
						<span class="tariff-card__badge-large-glass"></span>
						<span class="tariff-card__badge-large-text">
							{{ t('payment.discount', { n: discountPercent }) }}
						</span>
					</span>
				</div>
				<span class="tariff-card__price">
					{{ pricePerMonthFormatted }}
				</span>
			</div>

			<div
				v-if="showTotal"
				class="tariff-card__row tariff-card__row_total"
			>
				<span v-if="originalTotalFormatted" class="tariff-card__total-original">
					{{ originalTotalFormatted }}
				</span>
				<span class="tariff-card__total">{{ totalPriceFormatted }}</span>
			</div>

			<ul class="tariff-card__features">
				<li class="tariff-card__feature">
					<span class="tariff-card__feature-name">{{ t('payment.main_traffic') }}</span>
					<span class="tariff-card__feature-value">
						<core-icon
							name="infinity"
							viewBox="0 0 24 24"
							class="tariff-card__infinity"
						/>
					</span>
				</li>
				<li
					v-if="reserveMonthlyGb"
					class="tariff-card__feature"
				>
					<span class="tariff-card__feature-name">{{ t('payment.reserve_servers') }}</span>
					<span class="tariff-card__feature-value">
						{{ t('payment.reserve_per_month', { n: reserveMonthlyGb }) }}
					</span>
				</li>
			</ul>
		</div>
	</div>
</template>

<style lang="scss">
$card-bg-light: #fff;
$card-bg-light-selected: #faf8ff;

.tariff-card {
	position: relative;
	border-radius: 2.4rem;
	border: none;
	cursor: pointer;

	// ─── Pseudo-elements: border layers ───
	// ::before = default gray border (crossfades out)
	// ::after  = gradient border (crossfades in)
	&::before,
	&::after {
		content: '';
		position: absolute;
		inset: -1px;
		border-radius: 2.4rem;
		z-index: 0;
		pointer-events: none;
		transition: opacity 0.3s ease;
	}

	&::before {
		background: rgba(0, 28, 51, 0.12);
		opacity: 1;

		@include theme-dark {
			background: transparent;
			border: 1px solid rgba(255, 255, 255, 0.12);
		}
	}

	&::after {
		background: linear-gradient(196deg, rgba(200, 45, 252, 0.45) 0%, #792DFC 25%, #C82DFC 62%, rgba(121, 45, 252, 0.45) 100%);
		opacity: 0;

		@include theme-dark {
			background: linear-gradient(196deg, rgba(255, 255, 255) 0%, rgb(175, 129, 253) 25%, rgb(222, 129, 253) 62%, rgba(255, 255, 255) 100%);
			padding: 1px;
			-webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
			-webkit-mask-composite: xor;
			mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
			mask-composite: exclude;
		}
	}

	// ─── Modifiers ───
	&_gradient-border {
		&::before { opacity: 0; }
		&::after {
      opacity: 0.5;
      @include theme-dark {
        opacity: 0.8;
      }
    }
	}

	&_selected {
		&::before { opacity: 0; }
		&::after { opacity: 1; }

		.tariff-card__inner {
			background-color: $card-bg-light-selected;

			@include theme-dark {
				background-color: rgba(121, 45, 252, 0.16);
			}
		}
	}

	&_selected &__price {
		color: #792DFC;

		@include theme-dark {
			color: #BC96FD;
		}
	}

	// ─── Inner content wrapper ───
	&__inner {
		position: relative;
		z-index: 2;
		display: flex;
		flex-direction: column;
		gap: 0.8rem;
		padding: 1.2rem;
		background-color: $card-bg-light;
		border-radius: calc(2.4rem - 1px);
		transition: background-color 0.3s ease;

		@include theme-dark {
			background-color: transparent;
		}

		@include min-desktop {
			padding: 2rem 2.4rem;
		}
	}

	// ─── Layout ───
	&__row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		min-height: 2.4rem;

		// Полная цена под ценой за месяц, по правому краю
		&_total {
			justify-content: flex-end;
			gap: 0.6rem;
			min-height: 0;
			margin-top: -0.4rem;
		}
	}

	&__period {
		display: flex;
		align-items: center;
		gap: 0.8rem;
	}

	&__period-name {
		@include font-16-pro-medium;
		transition: color 0.3s ease;

		@include min-desktop {
			font-size: 2.1rem;
			line-height: 1;
		}
	}

	&__price {
		@include font-16-pro-medium;
		font-weight: bold;
		color: var(--color-text);
		transition: color 0.3s ease;

		@include min-desktop {
			font-size: 2.1rem;
			line-height: 1;
		}
	}

	&__total-original {
		@include font-12-pro-regular;
		text-decoration: line-through;
		opacity: 0.4;

		@include min-desktop {
			font-size: 1.4rem;
		}
	}

	&__total {
		@include font-12-pro-regular;
		opacity: 0.7;
		transition: color 0.3s ease;

		@include min-desktop {
			font-size: 1.4rem;
		}
	}

	// ─── Что входит в тариф ───
	// Отделено линией от цен: это описание, а не третья цена
	&__features {
		list-style: none;
		padding: 0;
		margin: 0;
		display: flex;
		flex-direction: column;
		gap: 0.6rem;
		padding-top: 0.8rem;
		border-top: 1px solid rgba(var(--color-text-rgb), 0.08);

		@include theme-dark {
			border-top-color: rgba(255, 255, 255, 0.1);
		}
	}

	&__feature {
		display: flex;
		align-items: center;
		gap: 0.6rem;
		@include font-12-pro-regular;

		@include min-desktop {
			font-size: 1.4rem;
		}

		// Точка-разделитель между названием и значением, как в макете
		&::before {
			content: none;
		}
	}

	&__feature-name {
		opacity: 0.7;

		&::after {
			content: '·';
			margin-left: 0.6rem;
		}
	}

	// Значение акцентным цветом: именно оно отличает тарифы друг от друга
	&__feature-value {
		display: inline-flex;
		align-items: center;
		color: var(--color-purple);
		font-weight: 500;

		@include theme-dark {
			color: #BC96FD;
		}
	}

	&__infinity {
		width: 1.8rem;
		height: 1.8rem;
		fill: currentColor;

		@include min-desktop {
			width: 2rem;
			height: 2rem;
		}
	}

	// ─── Small discount badge ───
	&__badge-small {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.6rem 1.2rem;
		border-radius: 10rem;
		height: 3rem;
		overflow: hidden;
		background: linear-gradient(197deg, #fff 0%, rgba(200, 45, 252, 0.12) 31%, rgba(121, 45, 252, 0.12) 58%, #fff 95%);
		-webkit-backface-visibility: hidden;
		backface-visibility: hidden;

		@include theme-dark {
			background: linear-gradient(197deg, #66369D 0%, #844B9D 100%);
		}
	}

	&__badge-small-text {
		@include font-12-pro-regular;
		font-weight: 500;
		background: linear-gradient(189deg, #fff 10%, #792DFC 25%, #C82DFC 77%, #fff 109%);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;

		@include theme-dark {
			background: none;
			-webkit-text-fill-color: #fff;
			color: #fff;
		}

		@include min-desktop {
			font-size: 1.4rem;
		}
	}

	// ─── Large discount badge (best value) ───
	&__badge-large {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.6rem 1.2rem;
		border-radius: 10rem;
		height: 3rem;
		position: relative;
		overflow: hidden;
	}

	&__badge-large-bg {
		position: absolute;
		inset: 0;
		border-radius: inherit;
		background: linear-gradient(196deg, #fff 0%, #792DFC 31%, #C82DFC 58%, #fff 95%);
	}

	&__badge-large-glass {
		position: absolute;
		inset: 0;
		border-radius: inherit;
		background: rgba(255, 255, 255, 0.12);
		backdrop-filter: blur(12px);
	}

	&__badge-large-text {
		position: relative;
		z-index: 1;
		@include font-12-pro-regular;
		font-weight: 500;
		color: #fff;

		@include min-desktop {
			font-size: 1.4rem;
		}
	}
}
</style>
